// Sidebar
//
// AppSidebar.js
// Sidebar con collapses para Settings, y links a Dashboard, Clients, etc.

import React, { useContext, useState } from 'react'
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom' // Importamos con alias
import { AuthContext } from '../context/AuthContext'

// Iconos
import DashboardIcon from '@mui/icons-material/Dashboard'
import PeopleIcon from '@mui/icons-material/People'
import SettingsIcon from '@mui/icons-material/Settings'
import StorefrontIcon from '@mui/icons-material/Storefront'
import BusinessIcon from '@mui/icons-material/Business'
import ChatIcon from '@mui/icons-material/Chat'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import GroupIcon from '@mui/icons-material/Group'
import CodeIcon from '@mui/icons-material/Code'

// Ejemplo de items (opcional si los usas en otro lugar)
export const navItems = [
  {
    category: 'Analytical',
    items: [{ name: 'Dashboard', icon: <DashboardIcon />, link: '/dashboard' }],
  },
  {
    category: 'Management',
    items: [
      { name: 'Clients', icon: <BusinessIcon />, link: '/clients' },
      { name: 'Products', icon: <StorefrontIcon />, link: '/products' },
      { name: 'Users', icon: <PeopleIcon />, link: '/users' },
    ],
  },
  {
    category: 'APPS',
    items: [
      { name: 'Chat', icon: <ChatIcon />, link: '/chat' },
      { name: 'Calendar', icon: <CalendarTodayIcon />, link: '/calendar' },
    ],
  },
  {
    category: 'Settings',
    items: [
      { name: 'Settings', icon: <SettingsIcon />, link: '/settings' },
      {
        name: 'Control Panel',
        icon: <AdminPanelSettingsIcon />,
        link: '/settings/controlpanel',
      },
      {
        name: 'Gestión de Consumidores',
        icon: <GroupIcon />,
        subItems: [
          { name: 'Crear Consumidor', link: '/settings/consumers/create' },
          { name: 'Carga Masiva', link: '/settings/consumers/bulk' },
        ],
      },
      {
        name: 'Gestión de códigos',
        icon: <CodeIcon />,
        link: '/settings/codes-management, ',
      },
    ],
  },
]

function AppSidebar({ isVisible = true }) {
  const { user } = useContext(AuthContext)
  const [openSettings, setOpenSettings] = useState(false)
  const [openConsumidores, setOpenConsumidores] = useState(false)

  const handleSettingsClick = () => {
    setOpenSettings((prev) => !prev)
  }

  // Si no hay user o no está visible, no mostramos nada
  if (!user || !isVisible) {
    return null
  }

  return (
    <Box
      component="aside"
      aria-label="Navegación lateral"
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        backgroundColor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <List disablePadding>
        {/* Bloque 1: Management */}
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', px: 2, py: 1 }}
        >
          Management
        </Typography>
        <ListItemButton component={RouterLink} to="/dashboard">
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
        <ListItemButton component={RouterLink} to="/clients">
          <ListItemIcon>
            <BusinessIcon />
          </ListItemIcon>
          <ListItemText primary="Clients" />
        </ListItemButton>
        <ListItemButton component={RouterLink} to="/products">
          <ListItemIcon>
            <StorefrontIcon />
          </ListItemIcon>
          <ListItemText primary="Products" />
        </ListItemButton>
        <ListItemButton component={RouterLink} to="/users">
          <ListItemIcon>
            <PeopleIcon />
          </ListItemIcon>
          <ListItemText primary="Users" />
        </ListItemButton>

        {/* Bloque 2: Apps */}
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', px: 2, py: 1 }}
        >
          Apps
        </Typography>
        <ListItemButton component={RouterLink} to="/chat">
          <ListItemIcon>
            <ChatIcon />
          </ListItemIcon>
          <ListItemText primary="Chat" />
        </ListItemButton>
        <ListItemButton component={RouterLink} to="/calendar">
          <ListItemIcon>
            <CalendarTodayIcon />
          </ListItemIcon>
          <ListItemText primary="Calendar" />
        </ListItemButton>

        {/* Bloque 3: Settings con Collapse */}
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', px: 2, py: 1 }}
        >
          Settings
        </Typography>
        <ListItemButton onClick={handleSettingsClick}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Settings" />
          {openSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemButton>
        <Collapse in={openSettings} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              component={RouterLink}
              to="/settings/controlpanel"
            >
              <ListItemIcon>
                <AdminPanelSettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Control Panel" />
            </ListItemButton>
            {/* Nuevo grupo para Gestión de Consumidores */}
            <ListItemButton
              onClick={() => setOpenConsumidores(!openConsumidores)}
            >
              <ListItemIcon>
                <GroupIcon />
              </ListItemIcon>
              <ListItemText primary="Gestión de Consumidores" />
              {openConsumidores ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>

            <Collapse in={openConsumidores} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{ pl: 6 }} // Mayor sangría para subitems
                  component={RouterLink}
                  to="/settings/consumers/create"
                >
                  <ListItemText primary="Crear Consumidor" />
                </ListItemButton>

                <ListItemButton
                  sx={{ pl: 6 }}
                  component={RouterLink}
                  to="/settings/consumers/bulk"
                >
                  <ListItemText primary="Carga Masiva" />
                </ListItemButton>
              </List>
            </Collapse>
            <ListItemButton
              sx={{ pl: 4 }}
              component={RouterLink}
              to="/settings/controlpanel/codes-management"
            >
              <ListItemIcon>
                <CodeIcon />
              </ListItemIcon>
              <ListItemText primary="Gestión de códigos" />
            </ListItemButton>
          </List>
        </Collapse>
      </List>
    </Box>
  )
}

export default AppSidebar
